import { EDoorWindowType, IWindowData } from '@/editor/data/model/model';
import { RegionData } from '@/editor/data/region/Region';
import { ESeparateWallType } from '@/editor/data/separateWall/SeparateWall';
import { ERegionType } from '@/editor/data/type';
import { LineSeg2d } from '@/editor/math/LineSeg2d';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { isClockwise, pointToString } from '@/editor/math/utils';
import { globalStores } from '@/editor/stores';
import { ELayoutWallType, ILayoutPathSeg } from '@/service/layout/types';
import { EEntityType, EFaceSide, EPointOnSegmentType, IWallEdge } from '@/editor/types';
import { getRegionArea } from '@/editor/utils/region';
import { clamp, groupBy } from 'lodash-es';
import { convertSeparateWallTypeToLayoutWallType } from './utils';
import { regionLayoutModelConfig } from '@/config/region-layout-model-config';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { RoomData } from '@/editor/data/room/Room';
import { getAreaInfo } from '../room/area';
import { thresholdValue } from '@/editor/math/const';
import { ArcSeg2d } from '@/editor/math/arcSeg2d';
import { getArcEdgeByRectBox } from '../arc';

export enum EAutoLayoutMode {
  NONE = 'n',
  FULL = 'f',
  PARTIAL = 'pa'
}

/**
 * 获取可进行布局的房间列表（有门的房间）
 * @returns 可布局的房间列表
 */
export function getLayoutableRoomList(forSelectEntrance: boolean) {
  return globalStores.roomStore
    .getRoomDatas()
    .map((roomData) => getFullLayoutInfo(roomData.id, forSelectEntrance))
    .filter((info) => info !== undefined);
}

/**
 * 获取面积最大的可布局房间信息
 * @returns 房间信息或 undefined
 */
export function getLargestLayoutableRoom() {
  const validRoomList = getLayoutableRoomList(true)
    .filter((item) => item.isValidRoom)
    .sort((a, b) => b.area - a.area);
  return validRoomList.length > 0 ? validRoomList[0] : undefined;
}

export const getLayoutInfo = (id: string, needDoor: boolean = true) => {
  const region = globalStores.regionStore.getRoomRegions(id)[0];

  if (region) {
    const doors = needDoor ? getRegionSegsAndDoors(region).doors : [];
    if (needDoor && doors.length === 0) {
      return undefined;
    }
    const area = getRegionArea(region);
    return { region, area, doors, roomId: id };
  }
  return undefined;
};

/** 获取全局自动布局的 list */
export function getFullLayoutInfo(roomId: string, forSelectEntrance: boolean = true) {
  const roomData = globalStores.roomStore.getRoomData(roomId);

  if (roomData) {
    const data = getFullLayoutData(roomData, forSelectEntrance);
    if (forSelectEntrance && data.doors.length === 0) {
      return undefined;
    }
    const area = getAreaInfo(roomData).coreArea;

    return { roomData, area, doors: data.doors, isValidRoom: data.isValidRoom };
  }

  return undefined;
}

const DOOR_TYPES: EDoorWindowType[] = [
  EDoorWindowType.singleDoor,
  EDoorWindowType.motherDoor,
  EDoorWindowType.doubleDoor
] as const;

interface ILayoutWindowData extends IWindowData {
  start: IPoint2d;
  end: IPoint2d;
  wallId: string;
  onWallWidth: number;
  isFullyOnWall: boolean;
}

export function getFullLayoutData(
  roomData: RoomData,
  forSelectEntrance = true
): {
  pathInfos: IWallEdge[];
  holeInfos: IWallEdge[][];
  doors: IWindowData[];
  isValidRoom: boolean;
} {
  const doorWindows = globalStores.modelStore.getWindowDatas().filter((doorWindow) => {
    return !forSelectEntrance || DOOR_TYPES.includes(doorWindow.type);
  });

  const { pathInfos, holeInfos } = roomData;
  const roomRelativeWallIdMap = new Map([...pathInfos, ...holeInfos.flat()].map(({ wallId }) => [wallId, wallId]));

  // 获取相连相连墙体
  pathInfos.forEach((path) => {
    const connectedWallInfo = globalStores.wallStore.getConnectedWallInfo(path.wallId);
    if (!connectedWallInfo) return;
    const { mainStartConnectToPoint, mainEndConnectToPoint } = connectedWallInfo;
    for (const wallItem of [...mainEndConnectToPoint, ...mainStartConnectToPoint]) {
      if (
        roomRelativeWallIdMap.has(wallItem.id) ||
        ![EPointOnSegmentType.StartPoint, EPointOnSegmentType.EndPoint].includes(wallItem.connectType)
      ) {
        continue;
      }
      if (
        path.center !== undefined &&
        wallItem.seg instanceof ArcSeg2d &&
        Point2dUtils.equals(wallItem.seg.center, path.center)
      ) {
        roomRelativeWallIdMap.set(wallItem.id, path.wallId);
      }
      if (path.center === undefined && wallItem.seg instanceof LineSeg2d) {
        const pathSeg = new LineSeg2d(path.start, path.end);
        if (pathSeg.isParallel(wallItem.seg)) {
          roomRelativeWallIdMap.set(wallItem.id, path.wallId);
        }
      }
    }
  });

  const doors = doorWindows
    .reduce<ILayoutWindowData[]>((acc, cur) => {
      const { relativeWallInfo, center, width } = cur;
      if (relativeWallInfo.wallType !== EEntityType.Wall || !roomRelativeWallIdMap.has(relativeWallInfo.relativeWallId))
        return acc;

      const realWallId = roomRelativeWallIdMap.get(relativeWallInfo.relativeWallId);
      const matchedPathInfos = pathInfos.filter(({ wallId }) => wallId === realWallId);
      const matchedHoleInfos = holeInfos.flat().filter(({ wallId }) => wallId === realWallId);
      const matchedPaths = matchedPathInfos.length > 0 ? matchedPathInfos : matchedHoleInfos;

      if (matchedPaths.length === 0) return acc;

      matchedPaths.forEach((matchedPath, index) => {
        if ([EFaceSide.StartSide, EFaceSide.EndSide].includes(matchedPath.side)) return acc;
        if (matchedPath.center && matchedPath.clockwise !== undefined) {
          const seg = new ArcSeg2d({
            start: matchedPath.start,
            end: matchedPath.end,
            center: matchedPath.center,
            clockwise: matchedPath.clockwise
          });

          const ratio = seg.getExactFootRatio(center);
          const newCenter = seg.getPointFromRatio(ratio);
          const edge = getArcEdgeByRectBox({ rectCenter: newCenter, rectWidth: width, circle: seg.createCircle() });

          if (!edge) return acc;
          const [edgeStartRatio, edgeEndRatio] = [seg.getFootRatio(edge.start), seg.getFootRatio(edge.end)].sort(
            (a, b) => a - b
          );

          if (edgeStartRatio > 1 || edgeEndRatio < 0) return acc;

          const isFullyOnWall = edgeStartRatio >= 0 && edgeEndRatio <= 1;
          if (forSelectEntrance && !isFullyOnWall) return acc;
          const startRatio = clamp(edgeStartRatio, 0, 1);
          const endRatio = clamp(edgeEndRatio, 0, 1);

          const start = seg.getPointFromRatio(Math.max(0, startRatio));
          const end = seg.getPointFromRatio(Math.min(1, endRatio));
          const onWallWidth = (Math.min(1, endRatio) - Math.max(0, startRatio)) * seg.length;

          acc.push({
            ...cur,
            center: newCenter,
            start,
            end,
            wallId: `${matchedPath.wallId}##${index}`,
            onWallWidth,
            isFullyOnWall
          });
        } else {
          const seg = new LineSeg2d(matchedPath.start, matchedPath.end);
          const ratio = seg.getFootRatio(center);

          const halfWidthRatio = width / 2 / seg.length;
          const startRatio = ratio - halfWidthRatio;
          const endRatio = ratio + halfWidthRatio;

          // 门窗没有任意部分存在于房间墙上
          if (startRatio > 1 || endRatio < 0) {
            return acc;
          }

          const isFullyOnWall = startRatio >= 0 && endRatio <= 1;
          if (forSelectEntrance && !isFullyOnWall) {
            return acc;
          }
          const newCenter = seg.getPointFromRatio(ratio);
          const start = seg.getPointFromRatio(Math.max(0, startRatio));
          const end = seg.getPointFromRatio(Math.min(1, endRatio));
          const onWallWidth = isFullyOnWall ? width : (Math.min(1, endRatio) - Math.max(0, startRatio)) * seg.length;

          acc.push({
            ...cur,
            center: newCenter,
            start,
            end,
            wallId: `${matchedPath.wallId}##${index}`,
            onWallWidth,
            isFullyOnWall
          });
        }
      });

      return acc;
    }, [])
    .sort((a, b) => b.onWallWidth - a.onWallWidth);

  const isValidRoom = doors.some((door) => DOOR_TYPES.includes(door.type) && door.isFullyOnWall);

  return {
    pathInfos: labelPathInfosWithIndex(pathInfos),
    holeInfos: labelHoleInfosWithIndex(holeInfos),
    doors,
    isValidRoom
  };
}

function labelPathInfosWithIndex(infos: IWallEdge[]): IWallEdge[] {
  const wallIdMap = new Map<string, number>();
  return infos.map((info) => {
    const { wallId } = info;
    const index = wallIdMap.get(wallId) ?? 0;
    wallIdMap.set(wallId, index + 1);
    return { ...info, wallId: `${wallId}##${index}` };
  });
}

function labelHoleInfosWithIndex(infos: IWallEdge[][]): IWallEdge[][] {
  const wallIdMap = new Map<string, number>();
  return infos.map((group) =>
    group.map((info) => {
      const { wallId } = info;
      const index = wallIdMap.get(wallId) ?? 0;
      wallIdMap.set(wallId, index + 1);
      return { ...info, wallId: `${wallId}##${index}` };
    })
  );
}

export function getRoomLayoutInfo(roomData: RoomData, onlyDoor = true) {
  const originalDoors: IWindowData[] = [];
  const { pathInfos, holeInfos } = roomData;
  const DOOR_TYPES: EDoorWindowType[] = [
    EDoorWindowType.singleDoor,
    EDoorWindowType.motherDoor,
    EDoorWindowType.doubleDoor
  ] as const;
  const validPathWallIds: string[] = [];
  const validHoleWallIds: string[] = [];
  const pathWalls = groupBy(pathInfos, (info) => info.wallId);
  const holeWalls = groupBy(holeInfos.flat(), (info) => info.wallId);
  for (const [wallId, infos] of Object.entries(pathWalls)) {
    const faceTypes = infos.map((info) => info.side);
    // 如果墙的边界面同时包含左右两侧，则认为该墙不在分区边界上，不能用于布局
    if (faceTypes.includes(EFaceSide.MainLeftSide) && faceTypes.includes(EFaceSide.MainRightSide)) {
      continue;
    }
    validPathWallIds.push(wallId);
  }
  for (const [wallId, infos] of Object.entries(holeWalls)) {
    const faceTypes = infos.map((info) => info.side);
    if (faceTypes.includes(EFaceSide.MainLeftSide) && faceTypes.includes(EFaceSide.MainRightSide)) {
      continue;
    }
    validHoleWallIds.push(wallId);
  }

  globalStores.modelStore.getWindowDatas().forEach((doorWindow) => {
    if (onlyDoor && !DOOR_TYPES.includes(doorWindow.type)) {
      return;
    }
    if (
      doorWindow.relativeWallInfo.wallType === EEntityType.Wall &&
      (validPathWallIds.includes(doorWindow.relativeWallInfo.relativeWallId) ||
        validHoleWallIds.includes(doorWindow.relativeWallInfo.relativeWallId))
    ) {
      originalDoors.push(doorWindow);
    }
  });

  const doors = originalDoors
    .map((door) => {
      const wallId = door.relativeWallInfo.relativeWallId;
      const center = door.center;

      const relatedPathInfos = pathInfos.filter((info) => info.wallId === wallId);

      if (relatedPathInfos.length > 0) {
        const closestPathInfo = relatedPathInfos
          .map((info) => {
            const thickness = globalStores.wallStore.getWallData(info.wallId)?.baseData.thickness;
            if (thickness === undefined) {
              return undefined;
            }
            const seg = new LineSeg2d(info.start, info.end);
            const foot = seg.getFootOfPerpendicular(center);
            const distance = Point2dUtils.distanceTo(foot, center);

            // 如果门和墙中线的距离超过墙厚的一半，则认为不在围合房间的墙段上
            if (Math.abs(thickness / 2 - distance) > thresholdValue) {
              return undefined;
            }

            return { distance, foot };
          })
          .filter((p) => p !== undefined)
          .sort((a, b) => a.distance - b.distance);

        // 如果没有找到合适的门位置，说明门虽然关联了分区边界上的墙，但位置不在围合房间的墙段上
        if (closestPathInfo.length === 0) {
          return undefined;
        }

        return {
          ...door,
          center: closestPathInfo[0].foot
        };
      }

      const relatibeHoleInfos = holeInfos.flat().filter((info) => info.wallId === wallId);

      if (relatibeHoleInfos.length > 0) {
        const closestPathInfo = relatibeHoleInfos
          .map((info) => {
            const thickness = globalStores.wallStore.getWallData(info.wallId)?.baseData.thickness;
            if (thickness === undefined) {
              return undefined;
            }
            const seg = new LineSeg2d(info.start, info.end);
            const foot = seg.getFootOfPerpendicular(center);
            const distance = Point2dUtils.distanceTo(foot, center);

            // 如果门和墙中线的距离超过墙厚的一半，则认为不在围合房间的墙段上
            if (Math.abs(thickness / 2 - distance) > thresholdValue) {
              return undefined;
            }
            return { distance, foot };
          })
          .filter((p) => p !== undefined)
          .sort((a, b) => a.distance - b.distance);

        // 如果没有找到合适的门位置，说明门虽然关联了分区边界上的墙，但位置不在围合房间的墙段上
        if (closestPathInfo.length === 0) {
          return undefined;
        }

        return {
          ...door,
          center: closestPathInfo[0].foot
        };
      }

      return door;
    })
    .filter((door) => door !== undefined); // 过滤掉不在围合房间墙段上的门

  const pathSegs = pathInfos.map(({ start, end, wallId }) => {
    const thickness = globalStores.wallStore.getWallData(wallId)?.baseData.thickness ?? 100;
    return { start, end, type: ELayoutWallType.Wall, thickness };
  });

  const holeSegs = holeInfos.map((holes) =>
    holes
      .map(({ start, end }) => ({ start, end }))
      .reduce<{ start: IPoint2d; end: IPoint2d }[]>((acc, curr) => {
        if (acc.length === 0) {
          return [curr];
        }

        const last = acc[acc.length - 1];
        const currSeg = new LineSeg2d(curr.start, curr.end);
        const lastSeg = new LineSeg2d(last.start, last.end);
        // 如果不在同一延长线上，直接添加
        if (!lastSeg.isOnExtendedLineOf(currSeg)) {
          acc.push(curr);
        } else {
          // 合并共线的边
          acc[acc.length - 1] = {
            start: last.start,
            end: curr.end
          };
        }
        return acc;
      }, [])
  );

  return {
    doors: doors.sort((a, b) => b.width - a.width),
    pathSegs: reverseSegsIfNotClockwise(mergeCollinearSegs(pathSegs)),
    holeSegs
  };
}

function reverseSegsIfNotClockwise(segments: { start: IPoint2d; end: IPoint2d }[]) {
  const points = segments.map((seg) => seg.start);
  if (!isClockwise(points)) {
    return segments
      .map((seg) => ({
        start: seg.end,
        end: seg.start
      }))
      .reverse();
  }
  return segments;
}

/** 合并同向共线的线段 */
function mergeCollinearSegs(segments: { start: IPoint2d; end: IPoint2d }[]) {
  if (segments.length === 0) {
    return [];
  }
  const mergedSegments: { start: IPoint2d; end: IPoint2d }[] = [segments[0]];
  for (let i = 1; i < segments.length; i++) {
    const last = mergedSegments[mergedSegments.length - 1];
    const curr = segments[i];
    const lastSeg = new LineSeg2d(last.start, last.end);
    const currSeg = new LineSeg2d(curr.start, curr.end);
    if (lastSeg.isOnExtendedLineOf(currSeg)) {
      mergedSegments[mergedSegments.length - 1] = {
        start: last.start,
        end: curr.end
      };
    } else {
      mergedSegments.push(curr);
    }
  }
  return mergedSegments;
}

export const getPartialLayoutInfo = (id?: string) => {
  const regionData = globalStores.regionStore.getRegionData(id);
  if (regionData) {
    const coreArea = getRegionArea(regionData);
    const regionType = regionData.regionInfo.type as ERegionType;

    // 分区内已存在的家具
    const existFurniture = globalStores.modelStore
      .getFurnitureInPathInfos(regionData.corePathInfos, regionData.coreHoleInfos)
      .reduce<Record<string, number>>((acc, furniture) => {
        if (!furniture.modelId) {
          return acc;
        }
        if (acc[furniture.modelId]) {
          acc[furniture.modelId] += 1;
        } else {
          acc[furniture.modelId] = 1;
        }
        return acc;
      }, {});

    // 仅根据办公卓桌判断是否为ABW区域
    const tableFurnitues = Object.keys(existFurniture).filter(
      (id) => globalStores.modelStore.getModel(id)?.type === 'component.class.two.table'
    );
    const abwFurnitureModels = regionLayoutModelConfig['workspace-abw'].flatMap((item) => item.modelIds);

    const isABW =
      tableFurnitues.length > 0 && !tableFurnitues.some((modelKey) => !abwFurnitureModels.includes(modelKey));
    return { region: regionData, area: coreArea, furnitures: existFurniture, isABW, regionType };
  }
  return undefined;
};

/**
 * 获取可用于布局的家具列表
 * @param regionType 分区类型
 * @param layoutType 布局类型
 * @returns 家具列表
 */
interface IGetRegionSegsAndDoorsReturn {
  doors: IWindowData[];
  pathSegs: ILayoutPathSeg[];
  holeSegs: { start: IPoint2d; end: IPoint2d }[][];
}

interface IRegionPathSegment {
  path: LineSeg2d;
  real: LineSeg2d;
  thickness: number;
  type: ELayoutWallType;
}

interface IGetWallSegs {
  sepWallSegMap: Map<string, IRegionPathSegment[]>;
  wallSegMap: Map<string, IRegionPathSegment[]>;
}

/** 获取轮廓上的墙线段和隔墙线段 */
const getWallSegs = (sepWallIds: string[], wallEdges: IWallEdge[]): IGetWallSegs => {
  const mergedIdSet = new Set<string>();

  // 合并边角共线的内轮廓，解决L型轮廓的拐角处生成的额外转接轮廓
  const mergeToSegs = (edges: IWallEdge[]) => {
    // 标记哪些边可以合并（与墙不平行的边）
    const edgesWithMergeFlag = edges.map((edge) => {
      const sepData =
        globalStores.separateWallStore.getSepData(edge.sepId || '', true) ??
        globalStores.separateWallStore.getSepData(edge.sepId || '', false);
      const wallData = globalStores.wallStore.getWallData(sepData?.relativeWallId || '');

      const mergeable =
        wallData &&
        !new LineSeg2d(wallData.baseData.start, wallData.baseData.end).isParallel(new LineSeg2d(edge.start, edge.end));

      return { ...edge, mergeable: !!mergeable };
    });

    // 合并共线且可合并的边
    const mergedEdges = edgesWithMergeFlag.reduce<typeof edgesWithMergeFlag>((acc, curr) => {
      if (acc.length === 0) {
        return [curr];
      }

      const last = acc[acc.length - 1];
      const currSeg = new LineSeg2d(curr.start, curr.end);
      const lastSeg = new LineSeg2d(last.start, last.end);

      // 如果不在同一延长线上，直接添加
      if (!lastSeg.isOnExtendedLineOf(currSeg)) {
        acc.push(curr);
        return acc;
      }

      // 选择更长的可合并边进行合并
      const shouldMergeCurr = curr.mergeable && lastSeg.length > currSeg.length;
      const shouldMergeLast = last.mergeable && currSeg.length >= lastSeg.length;

      if (shouldMergeCurr || shouldMergeLast) {
        const [kept, merged] = shouldMergeCurr ? [last, curr] : [curr, last];
        if (merged.sepId && kept.sepId !== merged.sepId) {
          mergedIdSet.add(merged.sepId);
        }
        acc[acc.length - 1] = { ...kept, start: last.start, end: curr.end };
      } else {
        acc.push(curr);
      }

      return acc;
    }, []);

    // 将边转换为线段
    return mergedEdges.map((edge, index) => {
      const nextEdge = mergedEdges[(index + 1) % mergedEdges.length];
      return new LineSeg2d(edge.start, nextEdge.start);
    });
  };

  const regionEdgeSegs = mergeToSegs(wallEdges);

  const clipSepWallBySegs = (segs: LineSeg2d[]) => {
    return sepWallIds
      .map((id) => {
        if (mergedIdSet.has(id)) {
          return undefined;
        }
        const sepData =
          globalStores.separateWallStore.getSepData(id, true) ?? globalStores.separateWallStore.getSepData(id, false);

        if (!sepData) {
          return undefined;
        }

        const sepLineSeg = new LineSeg2d(sepData.start, sepData.end);
        const collinearPathSegs = segs
          .filter((pathSeg) => pathSeg.isCollinear(sepLineSeg))
          .map((seg) => (sepLineSeg.isSegmentsSameDirection(seg) ? seg : new LineSeg2d(seg.end, seg.start)));
        return collinearPathSegs.map(({ start, end }) => ({ ...sepData, start, end }));
      })
      .filter((data) => data !== undefined)
      .flat();
  };

  // 把隔墙按照分区边界进行裁剪
  const clippedSeparateWalls = clipSepWallBySegs(regionEdgeSegs);

  const groupedBaseVirtualSeparateWalls = groupBy(
    clippedSeparateWalls.filter((w) => w.type === ESeparateWallType.BaseVirtualWall),
    (swd) => swd.relativeWallId
  );

  const sepWallSegMap = clippedSeparateWalls
    .filter((w) => w.type !== ESeparateWallType.BaseVirtualWall)
    .reduce<Map<string, IRegionPathSegment[]>>((acc, swd) => {
      const wallSeg = new LineSeg2d(swd.start, swd.end);
      const wallSegObj: IRegionPathSegment = {
        path: wallSeg,
        real: wallSeg,
        type: convertSeparateWallTypeToLayoutWallType(swd.type),
        thickness: swd.thickness
      };
      if (acc.has(swd.id)) {
        acc.get(swd.id)?.push(wallSegObj);
      } else {
        acc.set(swd.id, [wallSegObj]);
      }
      return acc;
    }, new Map<string, IRegionPathSegment[]>());

  const wallSegMap = Object.entries(groupedBaseVirtualSeparateWalls).reduce<Map<string, IRegionPathSegment[]>>(
    (acc, group) => {
      const [wallId, swdList] = group;
      const wallData = globalStores.wallStore.getWallData(wallId);
      if (!wallData) {
        return acc;
      }
      const wallSeg = new LineSeg2d(wallData.baseData.start, wallData.baseData.end);
      const getMappedSeg = (seg: LineSeg2d): IRegionPathSegment => {
        const isSameDirection = wallSeg.isSegmentsSameDirection(seg);
        const mappedStart = wallSeg.getFootOfPerpendicular(isSameDirection ? seg.start : seg.end);
        const mappedEnd = wallSeg.getFootOfPerpendicular(isSameDirection ? seg.end : seg.start);
        return {
          path: seg,
          real: new LineSeg2d(mappedStart, mappedEnd),
          type: ELayoutWallType.Wall,
          thickness: wallData.baseData.thickness
        };
      };
      if (swdList.length > 1) {
        const firstSeg = new LineSeg2d(swdList[0].start, swdList[0].end);
        const mappedSegs: IRegionPathSegment[] = [getMappedSeg(firstSeg)];
        for (let i = 1; i < swdList.length; i++) {
          const swSeg = new LineSeg2d(swdList[i].start, swdList[i].end);
          if (!wallSeg.isParallel(swSeg)) {
            return acc;
          }
          if (!firstSeg.isOnExtendedLineOf(swSeg)) {
            return acc;
          }
          mappedSegs.push(getMappedSeg(swSeg));
        }
        acc.set(wallId, mappedSegs);
        return acc;
      } else {
        const swSeg = new LineSeg2d(swdList[0].start, swdList[0].end);
        if (!wallSeg.isParallel(swSeg)) {
          return acc;
        }
        acc.set(wallId, [getMappedSeg(swSeg)]);
        return acc;
      }
    },
    new Map<string, IRegionPathSegment[]>()
  );

  return {
    sepWallSegMap,
    wallSegMap
  };
};

/**
 * 获取**组成分区的墙**上的门，或者门窗
 * @param regionData 分区数据
 * @param onlyDoor 是否只获取门，默认 true
 * @returns 门或者门窗数据列表
 *
 * @todo 目前在获取组成分区的墙上的门时，如果墙被分区内的墙分割了，门窗中点刚好处在分割墙的位置时，会导致门窗被判定不在墙上，可能需优化该逻辑
 */
export const getRegionSegsAndDoors = (regionData: RegionData, onlyDoor = true): IGetRegionSegsAndDoorsReturn => {
  const doorsInRegionWall: IWindowData[] = [];

  const allDoorsInWall: IWindowData[] = [];
  const allDoorsInSepWall: IWindowData[] = [];

  const DOOR_TYPES: EDoorWindowType[] = [
    EDoorWindowType.singleDoor,
    EDoorWindowType.motherDoor,
    EDoorWindowType.doubleDoor
  ] as const;

  globalStores.modelStore.getWindowDatas().forEach((doorWindow) => {
    if (onlyDoor && !DOOR_TYPES.includes(doorWindow.type)) {
      return;
    }
    if (doorWindow.relativeWallInfo.wallType === EEntityType.Wall) {
      allDoorsInWall.push(doorWindow);
    } else if (doorWindow.relativeWallInfo.wallType === EEntityType.SeparateWall) {
      allDoorsInSepWall.push(doorWindow);
    } else {
      console.warn('未知的门窗关联墙体类型', doorWindow);
    }
  });

  const wallSegsList = [regionData.corePathInfos, ...regionData.coreHoleInfos].map((pathInfo) =>
    getWallSegs(regionData.relateSeparateWallIds, pathInfo)
  );

  const { wallSegMap, sepWallSegMap } = wallSegsList.reduce(
    (final, current) => {
      current.wallSegMap.forEach((segs, wallId) => {
        const existSegs = final.wallSegMap.get(wallId);
        if (existSegs) existSegs.push(...segs);
        else final.wallSegMap.set(wallId, segs);
      });

      current.sepWallSegMap.forEach((segs, wallId) => {
        const existSegs = final.sepWallSegMap.get(wallId);
        if (existSegs) existSegs.push(...segs);
        else final.sepWallSegMap.set(wallId, segs);
      });

      return final;
    },
    {
      wallSegMap: new Map<string, IRegionPathSegment[]>(),
      sepWallSegMap: new Map<string, IRegionPathSegment[]>()
    }
  );

  // 遍历墙上的门，判断门是否在分区的墙线上

  allDoorsInWall
    .filter((door) => wallSegMap.has(door.relativeWallInfo.relativeWallId))
    .forEach((door) => {
      const wallSegs = wallSegMap.get(door.relativeWallInfo.relativeWallId);
      const originalWall = globalStores.wallStore.getWallData(door.relativeWallInfo.relativeWallId);
      if (wallSegs && originalWall) {
        const { distance: doorStartDistance, center } = door;
        const { start: wallStart } = originalWall.baseData;
        for (const wallSeg of wallSegs) {
          const segStartDistance = Point2dUtils.distanceTo(wallStart, wallSeg.real.start);
          const segEndDistance = Point2dUtils.distanceTo(wallStart, wallSeg.real.end);
          if (doorStartDistance >= segStartDistance && doorStartDistance <= segEndDistance) {
            // 将门的位置映射到分区边界线上
            const footPoint = wallSeg.path.getFootOfPerpendicular(center);
            doorsInRegionWall.push({ ...door, center: footPoint });
            break;
          }
        }
      }
    });

  allDoorsInSepWall
    .filter((door) => sepWallSegMap.has(door.relativeWallInfo.relativeWallId))
    .forEach((door) => {
      const wallSegs = sepWallSegMap.get(door.relativeWallInfo.relativeWallId);
      const originalWall = globalStores.separateWallStore.getSepData(door.relativeWallInfo.relativeWallId, false);
      if (wallSegs && originalWall) {
        const { distance: doorStartDistance } = door;
        const { start: wallStart } = originalWall;
        for (const wallSeg of wallSegs) {
          const segStartDistance = Point2dUtils.distanceTo(wallStart, wallSeg.path.start);
          const segEndDistance = Point2dUtils.distanceTo(wallStart, wallSeg.path.end);
          if (doorStartDistance >= segStartDistance && doorStartDistance <= segEndDistance) {
            doorsInRegionWall.push(door);
            break;
          }
        }
      }
    });

  const wallSeg = Array.from(wallSegsList[0].wallSegMap.values()).flatMap<ILayoutPathSeg>((wallSegs) => {
    return wallSegs.map((seg) => ({
      type: seg.type,
      thickness: seg.thickness,
      start: seg.path.start,
      end: seg.path.end
    }));
  });
  const sepWallSeg = Array.from(wallSegsList[0].sepWallSegMap.values()).flatMap<ILayoutPathSeg>((wallSegs) => {
    return wallSegs.map((seg) => ({
      type: seg.type,
      thickness: seg.thickness,
      start: seg.path.start,
      end: seg.path.end
    }));
  });
  const holeSegs = wallSegsList.slice(1).map((segs) =>
    Array.from(segs.wallSegMap.values())
      .map((wallSegs) => wallSegs.map((seg) => ({ start: seg.path.start, end: seg.path.end })))
      .flat()
  );

  return {
    doors: doorsInRegionWall.sort((a, b) => b.width - a.width),
    pathSegs: filterAndSortValidSegments([...wallSeg, ...sepWallSeg]),
    holeSegs
  };
};

/**
 * 反转线段列表，使其方向相反
 */
const reverseSegments = (segments: ILayoutPathSeg[]): ILayoutPathSeg[] => {
  return segments
    .map((seg) => ({
      ...seg,
      start: seg.end,
      end: seg.start
    }))
    .reverse();
};

/**
 * 过滤无效线段、连接成路径，并确保顺时针排序
 */
const filterAndSortValidSegments = (segments: ILayoutPathSeg[]): ILayoutPathSeg[] => {
  const validSegments = segments.filter((seg) => {
    const isPoint = Point2dUtils.equals(seg.start, seg.end);
    if (isPoint) {
      console.warn('Warning: Ignoring zero-length wall segment.', seg);
    }
    return !isPoint;
  });

  if (validSegments.length === 0) {
    return [];
  }

  const startPointMap = new Map<string, { seg: ILayoutPathSeg; index: number }[]>();
  const endPointMap = new Map<string, { seg: ILayoutPathSeg; index: number }[]>();

  const addSegToCache = (seg: ILayoutPathSeg, index: number) => {
    const startKey = pointToString(seg.start);
    const endKey = pointToString(seg.end);

    const startSegs = startPointMap.get(startKey);
    if (startSegs) startSegs.push({ seg, index });
    else startPointMap.set(startKey, [{ seg, index }]);

    const endSegs = endPointMap.get(endKey);
    if (endSegs) endSegs.push({ seg, index });
    else endPointMap.set(endKey, [{ seg, index }]);
  };

  validSegments.forEach(addSegToCache);

  const used = new Set<number>();
  const wallList: ILayoutPathSeg[] = [validSegments[0]];
  used.add(0);

  while (wallList.length < validSegments.length) {
    const latestWall = wallList[wallList.length - 1];
    const endKey = pointToString(latestWall.end);

    let found = false;

    // 优先查找 start 点匹配的线段
    const startMatches = startPointMap.get(endKey) ?? [];
    for (const { seg, index } of startMatches) {
      if (!used.has(index)) {
        wallList.push(seg);
        used.add(index);
        found = true;
        break;
      }
    }

    // 如果没找到，查找 end 点匹配的线段（需要反转）
    if (!found) {
      const endMatches = endPointMap.get(endKey) ?? [];
      for (const { seg, index } of endMatches) {
        if (!used.has(index)) {
          wallList.push({ ...seg, start: seg.end, end: seg.start });
          used.add(index);
          found = true;
          break;
        }
      }
    }

    // 还没找到，从未使用线段中找到最短的连接线段，并生成一条新的线段连接
    if (!found) {
      let minDistance = Infinity;
      let closestSegIndex = -1;
      let closestSegReversed = false;

      for (let i = 0; i < validSegments.length; i++) {
        if (used.has(i)) continue;

        const seg = validSegments[i];

        const distToStart = Point2dUtils.distanceTo(latestWall.end, seg.start);
        const distToEnd = Point2dUtils.distanceTo(latestWall.end, seg.end);
        if (distToStart < minDistance) {
          minDistance = distToStart;
          closestSegIndex = i;
          closestSegReversed = false;
        }

        if (distToEnd < minDistance) {
          minDistance = distToEnd;
          closestSegIndex = i;
          closestSegReversed = true;
        }
      }

      if (closestSegIndex !== -1) {
        const closestSeg = validSegments[closestSegIndex];
        const newSeg = {
          start: latestWall.end,
          end: closestSegReversed ? closestSeg.end : closestSeg.start,
          type: closestSeg.type,
          thickness: closestSeg.thickness
        };
        wallList.push(newSeg);
        const index = validSegments.length;
        used.add(index);
        addSegToCache(newSeg, index);
      }
    }
  }

  const points = wallList.map((seg) => seg.start);
  const sortedWallList = isClockwise(points) ? wallList : reverseSegments(wallList);
  return sortedWallList;
};
